import React, { useState, useEffect } from 'react'
import styled from 'styled-components'

import { LG, MD } from '@zendeskgarden/react-typography'
import ChevronRightFill from '@zendeskgarden/svg-icons/src/16/chevron-right-fill.svg'
import ChevronDownFill from '@zendeskgarden/svg-icons/src/16/chevron-down-fill.svg'
import { Skeleton } from '@zendeskgarden/react-loaders'
import { Row } from '@zendeskgarden/react-grid'

import MockTooltip from './Tooltip'
import genDays from './genDays'
import BrandedLogos from './BrandedLogos'
import StatusIcon from './StatusIcon'
import { fetchWithTimeout, handleResponseErrors } from '../../utils/http'

const LoaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const LoaderContainer = styled.div`
  width: "918px";
  margin-bottom: 1px;
  border-style: solid;
  border-width: 1px;
  border-color: #C2C8CC;
  padding: 10px;
  margin-left: "10px";
`

const StatusGraphContainer = styled.div`
  margin-bottom: ${props => props.subservice ? "default" : "15px"};
  width: 950px;
  //There is some slight bugginess where other services will re-expand when you collapse another
  //change none->expand in the ternary below to re-enable
  animation-name: ${props => props.isParentToggleOpen ? "none" : "none"};
  animation-duration: 750ms;
  animation-timing-function: ease;
`

const StatusGraphWrapper = styled.div`
  width: ${props => props.subservice ? "940px" : "950px"};
  display: flex;
  margin-bottom: 1px;
  border-style: solid;
  border-width: 1px;
  border-color: #C2C8CC;
  padding: 10px;
  margin-left: ${props => props.subservice ? "10px" : "default"};
`

const LeanViewStatusGraphWrapper = styled.div`
  width: ${props => props.subservice ? "940px" : "950px"};
  display: flex;
  margin-bottom: 1px;
  border-style: solid;
  border-width: 1px;
  border-color: #C2C8CC;
  padding-top: 20px;
  padding-bottom: 15px;
  padding-right: 10px;
  padding-left: 10px;
  margin-left: ${props => props.subservice ? "10px" : "default"};
`

const StatusToggleBox = styled.div`
  width: 30px;
  margin-bottom: 20px;
`

const ChevronWrapper = styled.div`
  margin-top: 2px;
  display: inline-block;
`

const ChevronButton = styled.button`
  margin-top: 2px;
  border: none;
  background-color: inherit;
`

const StatusGraph = styled.div`
  width: 900px;
`

const ServiceChartHeader = styled.div`
  display: flex;
  margin-bottom: 10px;
  justify-content: space-between;
`

const ServiceChartWrapper = styled.div`
  height: 40px;
  width: 900px;
  vertical-align: middle;
  display: inline-block;
`

const StyledDay = styled.div`
  display: inline-block;
  height: ${props =>
    props.validation === 'outage' ? '60%' : props.validation === 'degradation' ? '80%' : '100%'}
  vertical-align: middle;
  width: 8px;
  margin-right: 2px;
  justify-content: space-between;
  cursor: ${props =>
    ['outage', 'degradation'].includes(props.validation) ? 'pointer' : 'default'}
  background-color: ${props =>
    props.validation === 'normal' ?
      "#5EAE91" :
      props.validation === 'outage' ?
        "#E35B66" :
        "#FFB648"}
`

const ServiceChartFooter = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
`

const CurrentServiceStatusBanner = styled(LG)`
  margin-right: 15px
`

const StyledIcon = styled(LG)`
  margin-left: 15px
  margin-right: 5px
`

const ServiceChart = ({ service, incidents, incidentServices, inTimezone, asOfDate, daysBack, rangeOffset, expandedServices, updateExpandedServices, isLeanDisplay, dateOffsetBugfixEnabled }) => {
  const [loadingSubservices, setLoadingSubservices] = useState([])
  const [subservices, setSubservices] = useState([])
  const [isParentToggleOpen, setIsParentToggleOpen] = useState(expandedServices.includes(service.id.toString()))

  useEffect(() => {
    const fetchSubservices = async () => {
      setLoadingSubservices(true)
      try {
        const res = await fetchWithTimeout(`api/ssp/services.json?parent_id=${service.id}`)
        const body = await res.json()
        handleResponseErrors(res, body)
        setSubservices(body.data)
      } catch (error) {
        console.log(error)
      } finally {
        setLoadingSubservices(false)
      }
    }

    if (isParentToggleOpen && subservices.length === 0) {
      fetchSubservices()
    }
  }, [isParentToggleOpen])

  const days = (infoByDay, inTimezone) => Object.keys(infoByDay).map(dayKey => {
    const info = infoByDay[dayKey]
    return (
      info.status === 'normal' ?
        <StyledDay key={`${service.id.toString()}-${dayKey}`} validation={info.status} />          :
        <MockTooltip
          key={`${service.id.toString()}-${dayKey}`}
          incidents={info.incidents}
          incidentServices={incidentServices}
          serviceId={service.id}
          inTimezone={inTimezone}
          validation={info.status}
        >
          <StyledDay validation={info.status} />
        </MockTooltip>
    )
  })

  const fmtDaysAgo = num => `${num} days ago`
  // TODO: this gets called alot of times on render, maybe use a useCallback to memoize?
  // console.log("rangeOffset=", rangeOffset)

  const rangeStartText = fmtDaysAgo(-(rangeOffset - daysBack))
  const rangeEndText = rangeOffset === 0 ? "Today" : fmtDaysAgo(-rangeOffset+1)

  const SubserviceGraphs = ({ isParentToggleOpen }) => {
    if (loadingSubservices) {
      return (
        <LoaderContainer>
          <LoaderWrapper>
            <Skeleton/>
          </LoaderWrapper>
        </LoaderContainer>
      )
    }
    return (
      subservices.map(subservice => {
        const subserviceInfoByDay = genDays(subservice, incidents, incidentServices, inTimezone, asOfDate, daysBack, dateOffsetBugfixEnabled)
        return (
          <StatusGraphContainer key={subservice.id} subservice="true" isParentToggleOpen={isParentToggleOpen}>
            <StatusGraphWrapper subservice="true">
              <StatusToggleBox/>
              <StatusGraph>
                <ServiceChartHeader>
                  <LG>{subservice.attributes.name}</LG>
                </ServiceChartHeader>
                <ServiceChartWrapper>{days(subserviceInfoByDay, inTimezone)}</ServiceChartWrapper>
                <ServiceChartFooter>
                  <MD>{rangeStartText}</MD>
                  <MD>{rangeEndText}</MD>
                </ServiceChartFooter>
              </StatusGraph>
            </StatusGraphWrapper>
          </StatusGraphContainer>
        )
      })
    )
  }

  const serviceInfoByDay = genDays(service, incidents, incidentServices, inTimezone, asOfDate, daysBack, dateOffsetBugfixEnabled)

  const currentStatus = () => {
    const inc = genDays(service, incidents, incidentServices, inTimezone, asOfDate, 1)
    const incidentService = incident => { return incidentServices.filter(i => i.attributes.serviceId.toString() === service.id && i.attributes.incidentId.toString() === incident.id)[0] }

    let todayInfo = {}

    Object.keys(inc).map(day =>{
      todayInfo = inc[day]
    })

    const ongoingIncidents = todayInfo.incidents.filter(incident => (incident.attributes.resolvedAt === null && incidentService(incident) && incidentService(incident).attributes.resolvedAt == null))
    const ongoingCount = ongoingIncidents.length

    const todayStatus =
      (() => {
        if (ongoingCount === 0) {
          return ('normal')
        } else if (ongoingIncidents.filter(
          incident => {
            return incidentService(incident).attributes.outage === true
          }).length > 0) {
          return ('outage')
        }
        return ('degradation')
      })()

    const incidentText = (ongoingCount > 1) ? 'active incidents' : 'active incident'

    const currStatusText = (status, ongoingCount, incidentText) => {
      return (
        <Row>
          <StyledIcon>
            <StatusIcon status={status} />
          </StyledIcon>
          {(ongoingCount === 0) ? 'No active incidents' : `${ongoingCount  } ${  incidentText}`}
        </Row>
      )
    }

    return (
      <CurrentServiceStatusBanner validation = {todayStatus}>
        {(todayStatus === 'normal' || !isLeanDisplay)?
          currStatusText(todayStatus, ongoingCount, incidentText)        :
          <MockTooltip
            key={`${service.id.toString()}`}
            incidents={ongoingIncidents}
            incidentServices={incidentServices}
            serviceId={service.id}
            inTimezone={inTimezone}
            validation={todayStatus}
          >
            {currStatusText(todayStatus, ongoingCount, incidentText)}
          </MockTooltip>
        }
      </CurrentServiceStatusBanner>
    )
  }

  const handleChevronToggle = (serviceID, targetToggleState) => {
    setIsParentToggleOpen(targetToggleState)
    updateExpandedServices(serviceID.toString(), targetToggleState)
  }

  const DetailedStatusGraph = () => {
    return (
      <StatusGraphWrapper key={service.id}>
        <StatusToggleBox>
          <ChevronWrapper>
            {service.attributes.hasSubservices ?
              <ChevronButton
                id={`chevron-${service.attributes.slug}`}
                onClick={() => handleChevronToggle(service.id, !isParentToggleOpen)}>
                {isParentToggleOpen ? <ChevronDownFill /> : <ChevronRightFill />}
              </ChevronButton> :
              null}
          </ChevronWrapper>
        </StatusToggleBox>
        <StatusGraph>
          <ServiceChartHeader>
            <BrandedLogos serviceName={service.attributes.name} />
            {currentStatus()}
          </ServiceChartHeader>
          <ServiceChartWrapper>{days(serviceInfoByDay, inTimezone)}</ServiceChartWrapper>
          <ServiceChartFooter>
            <MD>{rangeStartText}</MD>
            <MD>{rangeEndText}</MD>
          </ServiceChartFooter>
        </StatusGraph>
      </StatusGraphWrapper>
    )
  }

  const LeanStatusGraph = () => {
    return (
      <LeanViewStatusGraphWrapper key={service.id}>
        <StatusToggleBox>
        </StatusToggleBox>
        <StatusGraph>
          <ServiceChartHeader>
            <BrandedLogos serviceName={service.attributes.name} />
            {currentStatus()}
          </ServiceChartHeader>
        </StatusGraph>
      </LeanViewStatusGraphWrapper>
    )
  }

  return (
    <StatusGraphContainer>
      { (isLeanDisplay ? <LeanStatusGraph /> : <DetailedStatusGraph />) }
      { isParentToggleOpen && !isLeanDisplay && <SubserviceGraphs isParentToggleOpen={isParentToggleOpen} /> }
    </StatusGraphContainer>
  )
}

export default ServiceChart
